import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';
import type { ServiceTypeSlug } from '@rover/types/src/ServiceType';

import {
  type SitterProfileVersion,
  type ViewMode,
  RevampProfileContext,
} from '../hooks/useRevampProfileContext';
import useSittersRatesToggle from '../hooks/useSittersRatesToggle';

interface RevampProfileProviderProps {
  children: ReactNode;
  serviceType: string | undefined;
  setServiceType: (serviceType: string) => void;
  sitterData: EnhancedPersonSitterProfile;
  viewMode: ViewMode;
  pageTreatment: SitterProfileVersion;
  contactUrl: string;
  isShowingPriceInCurrency?: boolean;
}

export const RevampProfileProvider = ({
  children,
  serviceType,
  setServiceType,
  sitterData,
  viewMode,
  pageTreatment,
  contactUrl,
  isShowingPriceInCurrency,
}: RevampProfileProviderProps): JSX.Element => {
  const [reviewsModalOpen, setReviewsModalOpen] = useState(false);
  const { showSittersRates, onToggleSittersRates } = useSittersRatesToggle(
    isShowingPriceInCurrency,
    sitterData.authenticatedContext?.shouldShowBhToggle
  );
  const priceDisclaimerShownRef = useRef(false);
  const { setServiceType: setShortlistServiceType } = useShortlist();

  // The shortlist and multi-contact page read the service from the shortlist store, not the page.
  const selectServiceType = useCallback(
    (slug: string) => {
      setServiceType(slug);
      const service = sitterData.services.find((s) => s.slug === slug);
      setShortlistServiceType(
        slug as ServiceTypeSlug,
        !!service?.serviceType.supportsRecurringBilling
      );
    },
    [setServiceType, setShortlistServiceType, sitterData.services]
  );

  const contextValue = useMemo(
    () => ({
      serviceType,
      setServiceType: selectServiceType,
      sitterData,
      viewMode,
      pageTreatment,
      reviewsModalOpen,
      setReviewsModalOpen,
      contactUrl,
      showSittersRates,
      onToggleSittersRates,
      priceDisclaimerShownRef,
    }),
    [
      serviceType,
      selectServiceType,
      sitterData,
      viewMode,
      pageTreatment,
      reviewsModalOpen,
      contactUrl,
      showSittersRates,
      onToggleSittersRates,
      priceDisclaimerShownRef,
    ]
  );

  return (
    <RevampProfileContext.Provider value={contextValue}>{children}</RevampProfileContext.Provider>
  );
};
